import { z } from "zod";
import { CalendarDateSchema } from "./campaign-creation";

const AsinSchema = z
  .string()
  .trim()
  .regex(/^[A-Z0-9]{10}$/);

const NullableTargetBidSchema = z.number().positive().nullable();

export const SponsoredDisplayOptimizationGoalSchema = z.enum([
  "page_visits",
  "conversions",
  "viewable_impressions",
]);
export type SponsoredDisplayOptimizationGoal = z.infer<
  typeof SponsoredDisplayOptimizationGoalSchema
>;

/**
 * The client chooses a product goal; Amazon's bid optimization and cost type
 * are deterministic execution details. A submit payload never accepts either
 * derived field independently.
 */
export const SPONSORED_DISPLAY_OPTIMIZATION_BY_GOAL = {
  page_visits: { bidOptimization: "clicks", costType: "cpc" },
  conversions: { bidOptimization: "conversions", costType: "cpc" },
  viewable_impressions: { bidOptimization: "reach", costType: "vcpm" },
} as const satisfies Record<
  SponsoredDisplayOptimizationGoal,
  {
    bidOptimization: "clicks" | "conversions" | "reach";
    costType: "cpc" | "vcpm";
  }
>;

export function sponsoredDisplayOptimizationFor(goal: SponsoredDisplayOptimizationGoal) {
  return SPONSORED_DISPLAY_OPTIMIZATION_BY_GOAL[goal];
}

const NumericRefinementSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("greater_than"), value: z.number().nonnegative() }).strict(),
  z.object({ type: z.literal("less_than"), value: z.number().positive() }).strict(),
  z
    .object({
      type: z.literal("between"),
      min: z.number().nonnegative(),
      max: z.number().positive(),
    })
    .strict(),
]);

const ReviewRatingRefinementSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("greater_than"), value: z.number().min(0).max(5) }).strict(),
  z.object({ type: z.literal("less_than"), value: z.number().min(0).max(5) }).strict(),
  z
    .object({
      type: z.literal("between"),
      min: z.number().min(0).max(5),
      max: z.number().min(0).max(5),
    })
    .strict(),
]);

const CategoryRefinementsSchema = z
  .object({
    brandId: z.string().trim().min(1).nullable().default(null),
    price: NumericRefinementSchema.nullable().default(null),
    reviewRating: ReviewRatingRefinementSchema.nullable().default(null),
    primeShippingOnly: z.boolean().default(false),
  })
  .strict()
  // Validated at the refinement level so every consumer — submit payloads and
  // the discovery request schemas alike — rejects an inverted range before it
  // can be serialized into an Amazon expression.
  .superRefine((refinements, ctx) => {
    if (refinements.price?.type === "between" && refinements.price.min >= refinements.price.max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Price refinement minimum must be lower than maximum",
        path: ["price"],
      });
    }
    if (
      refinements.reviewRating?.type === "between" &&
      refinements.reviewRating.min >= refinements.reviewRating.max
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Review-rating minimum must be lower than maximum",
        path: ["reviewRating"],
      });
    }
  })
  .default({
    brandId: null,
    price: null,
    reviewRating: null,
    primeShippingOnly: false,
  });

const CategorySourceSchema = z
  .object({
    type: z.literal("category"),
    categoryId: z.string().trim().min(1),
    refinements: CategoryRefinementsSchema,
  })
  .strict();

export const SponsoredDisplayContextualTargetSchema = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("product"),
      asin: AsinSchema,
      bid: NullableTargetBidSchema,
    })
    .strict(),
  CategorySourceSchema.extend({ bid: NullableTargetBidSchema }),
  z.object({ type: z.literal("similar_product"), bid: NullableTargetBidSchema }).strict(),
]);
export type SponsoredDisplayContextualTarget = z.infer<
  typeof SponsoredDisplayContextualTargetSchema
>;

export const SponsoredDisplayViewsRemarketingSourceSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("exact_product") }).strict(),
  z.object({ type: z.literal("similar_product") }).strict(),
  CategorySourceSchema,
]);
export type SponsoredDisplayViewsRemarketingSource = z.infer<
  typeof SponsoredDisplayViewsRemarketingSourceSchema
>;

export const SponsoredDisplayPurchasesRemarketingSourceSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("exact_product") }).strict(),
  z.object({ type: z.literal("related_product") }).strict(),
  CategorySourceSchema,
]);
export type SponsoredDisplayPurchasesRemarketingSource = z.infer<
  typeof SponsoredDisplayPurchasesRemarketingSourceSchema
>;

export const SponsoredDisplayViewsLookbackDaysSchema = z.union([
  z.literal(7),
  z.literal(14),
  z.literal(30),
  z.literal(60),
  z.literal(90),
]);

export const SponsoredDisplayPurchasesLookbackDaysSchema = z.union([
  z.literal(7),
  z.literal(14),
  z.literal(30),
  z.literal(60),
  z.literal(90),
  z.literal(180),
  z.literal(365),
]);

export const SponsoredDisplayAudienceTargetSchema = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("amazon_audience"),
      audienceId: z.string().trim().min(1),
      bid: NullableTargetBidSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal("views_remarketing"),
      source: SponsoredDisplayViewsRemarketingSourceSchema,
      lookbackDays: SponsoredDisplayViewsLookbackDaysSchema,
      bid: NullableTargetBidSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal("purchases_remarketing"),
      source: SponsoredDisplayPurchasesRemarketingSourceSchema,
      lookbackDays: SponsoredDisplayPurchasesLookbackDaysSchema,
      bid: NullableTargetBidSchema,
    })
    .strict(),
]);
export type SponsoredDisplayAudienceTarget = z.infer<typeof SponsoredDisplayAudienceTargetSchema>;

export const SponsoredDisplayTargetingSchema = z.discriminatedUnion("type", [
  z
    .object({
      type: z.literal("contextual"),
      targets: z.array(SponsoredDisplayContextualTargetSchema).min(1).max(1_000),
    })
    .strict(),
  z
    .object({
      type: z.literal("audience"),
      targets: z.array(SponsoredDisplayAudienceTargetSchema).min(1).max(1_000),
    })
    .strict(),
]);
export type SponsoredDisplayTargeting = z.infer<typeof SponsoredDisplayTargetingSchema>;

const CropSchema = z
  .object({
    top: z.number().int().nonnegative(),
    left: z.number().int().nonnegative(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
  })
  .strict();

const AssetReferenceSchema = z
  .object({
    assetId: z.string().trim().min(1),
    assetVersion: z.string().trim().min(1),
  })
  .strict();

const LogoSchema = AssetReferenceSchema.extend({ crop: CropSchema.nullable().default(null) });

const CustomImageSchema = AssetReferenceSchema.extend({
  /** Amazon requires both variants to use this same asset ID and version. */
  rectCrop: CropSchema,
  squareCrop: CropSchema,
});

export const SponsoredDisplayCreativeSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("dynamic") }).strict(),
  z
    .object({
      type: z.literal("custom"),
      headline: z.string().trim().min(1).max(50).nullable().default(null),
      logo: LogoSchema.nullable().default(null),
      image: CustomImageSchema.nullable().default(null),
      consentToTranslate: z.boolean().default(false),
    })
    .strict(),
]);
export type SponsoredDisplayCreative = z.infer<typeof SponsoredDisplayCreativeSchema>;

const SponsoredDisplayCreationPayloadSchema = z
  .object({
    adProduct: z.literal("sd"),
    requestId: z.string().uuid(),
    campaign: z
      .object({
        /** Desired state after PAUSED creation and authoritative readback. */
        desiredState: z.enum(["PAUSED", "ENABLED"]).default("ENABLED"),
        name: z.string().trim().min(1),
        portfolioId: z.string().trim().min(1).nullable(),
        startDate: CalendarDateSchema,
        endDate: CalendarDateSchema.nullable(),
        dailyBudget: z.number().positive(),
        optimizationGoal: SponsoredDisplayOptimizationGoalSchema,
      })
      .strict(),
    adGroup: z
      .object({
        name: z.string().trim().min(1),
        defaultBid: z.number().positive(),
      })
      .strict(),
    advertisedProducts: z
      .array(
        z
          .object({
            asin: AsinSchema,
            sku: z.string().trim().min(1).nullable(),
          })
          .strict(),
      )
      .min(1)
      .max(500),
    targeting: SponsoredDisplayTargetingSchema,
    creative: SponsoredDisplayCreativeSchema,
  })
  .strict();

function validatePayload(
  submission: z.infer<typeof SponsoredDisplayCreationPayloadSchema>,
  ctx: z.RefinementCtx,
): void {
  if (submission.campaign.endDate && submission.campaign.endDate < submission.campaign.startDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "endDate must be on or after startDate",
      path: ["campaign", "endDate"],
    });
  }

  const productKeys = submission.advertisedProducts.map(
    (product) => `${product.asin}\u0000${product.sku ?? ""}`,
  );
  if (new Set(productKeys).size !== productKeys.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Advertised products must be unique",
      path: ["advertisedProducts"],
    });
  }

  if (submission.creative.type === "custom" && submission.advertisedProducts.length > 100) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Custom creative supports at most 100 advertised products",
      path: ["advertisedProducts"],
    });
  }

  if (
    submission.creative.type === "custom" &&
    submission.creative.headline === null &&
    submission.creative.logo === null &&
    submission.creative.image === null
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Custom creative requires a headline, logo, or image",
      path: ["creative"],
    });
  }
}

/** Persisted requests remain resumable after their original start date passes. */
export const SponsoredDisplayCreationPersistedPayloadSchema =
  SponsoredDisplayCreationPayloadSchema.superRefine(validatePayload);
export type SponsoredDisplayCreationPersistedPayload = z.infer<
  typeof SponsoredDisplayCreationPersistedPayloadSchema
>;

export const SponsoredDisplayCreationSubmitSchema =
  SponsoredDisplayCreationPersistedPayloadSchema.superRefine((submission, ctx) => {
    const todayUtc = new Date().toISOString().slice(0, 10);
    if (submission.campaign.startDate < todayUtc) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "startDate cannot be in the past",
        path: ["campaign", "startDate"],
      });
    }
  });
export type SponsoredDisplayCreationSubmit = z.infer<typeof SponsoredDisplayCreationSubmitSchema>;
